import { LIMITS } from '@spotnote/shared/types'
import type { CaptureError } from '@spotnote/shared'
import type { Api, UploadTicket } from './api'

/**
 * Captures the page as the client sees it, once per session.
 *
 * Two artifacts, deliberately ordered:
 *
 *   1. the rrweb DOM snapshot — the source of truth for the replica, and the
 *      smaller of the two, so it goes first and alone on the critical path
 *   2. a full-page raster — what the client actually saw, and the source for
 *      every note crop. Started only once the snapshot is safely uploaded.
 *
 * Everything here is best-effort. A client who cannot upload either artifact
 * must still be able to send their notes; the designer just loses a picture.
 */

export interface SessionCapture {
  captureId?: string
  /** Why the snapshot did not make it, as far as this browser could tell. */
  error?: CaptureError
  /** Settles when the raster has uploaded or given up. Never rejects. */
  raster: Promise<void>
}

/** How long Send is willing to wait for a raster that is still encoding. */
export const RASTER_WAIT_MS = 4000

export async function captureSession(api: Api, base: string): Promise<SessionCapture> {
  const ticket = await api.capture()
  if (!ticket.capture_id || !ticket.upload) return { error: 'no_ticket', raster: Promise.resolve() }

  const error = await uploadSnapshot(base, ticket.upload)

  return {
    // Kept even when the upload looked like a failure: a bucket without CORS
    // stores the file and only hides the answer from us. The server checks
    // the bucket and links whatever really arrived.
    captureId: ticket.capture_id,
    ...(error ? { error } : {}),
    raster:
      !error && ticket.raster_upload
        ? whenIdle().then(() => uploadRaster(base, ticket.raster_upload!))
        : Promise.resolve(),
  }
}

/**
 * Assembles the upload form: signed fields first, then the file part last.
 * The order and the `set` Content-Type date from uploading straight to the
 * store with a POST policy; /api/w/upload accepts the same form, so widgets
 * cached from that time keep working unchanged.
 */
function policyForm(ticket: UploadTicket, file: Blob, name: string, type: string): FormData {
  const form = new FormData()
  for (const [key, value] of Object.entries(ticket.fields)) form.append(key, value)
  form.set('Content-Type', type)
  form.append('file', new File([file], name, { type }))
  return form
}

/** Resolves a promise but gives up after `ms`, so Send is never held hostage. */
export function withDeadline(promise: Promise<unknown>, ms: number): Promise<void> {
  return Promise.race([
    promise.then(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, ms)),
  ])
}

/** Null on success, otherwise what went wrong. */
async function uploadSnapshot(base: string, ticket: UploadTicket): Promise<CaptureError | null> {
  let body: Blob
  try {
    const { serialize } = (await import(
      /* @vite-ignore */ new URL('/w-snapshot.js', base).href
    )) as { serialize: () => string }
    body = await gzip(serialize())
  } catch (cause) {
    console.warn('[spotnote] snapshot capture failed, sending notes without it', cause)
    return 'capture_failed'
  }

  if (body.size > LIMITS.maxSnapshotBytes) {
    console.warn('[spotnote] snapshot exceeds the size cap, sending notes without it')
    return 'too_large'
  }

  try {
    const response = await fetch(ticket.url, {
      method: 'POST',
      body: policyForm(ticket, body, 'snapshot.json.gz', 'application/json'),
    })
    return response.ok ? null : 'upload_rejected'
  } catch (cause) {
    console.warn('[spotnote] snapshot upload failed, sending notes without it', cause)
    return 'upload_blocked'
  }
}

async function uploadRaster(base: string, ticket: UploadTicket): Promise<void> {
  try {
    const { rasterize } = (await import(/* @vite-ignore */ new URL('/w-raster.js', base).href)) as {
      rasterize: () => Promise<{ blob: Blob }>
    }

    const { blob } = await rasterize()
    if (blob.size > LIMITS.maxRasterBytes) {
      console.warn('[spotnote] page image exceeds the size cap, skipping it')
      return
    }

    await fetch(ticket.url, {
      method: 'POST',
      body: policyForm(ticket, blob, 'raster', blob.type || 'image/webp'),
    })
  } catch (cause) {
    // Tainted canvas is the common case. The designer app still rasterises the
    // replay itself as a fallback, so this is a lost optimisation, not a lost
    // capability.
    console.warn('[spotnote] page image failed, continuing without it', cause)
  }
}

/**
 * Waits for the browser to be idle before starting the expensive work — the
 * client is mid-annotation on someone else's site and should not feel us.
 */
function whenIdle(): Promise<void> {
  return new Promise((resolve) => {
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: object) => void })
      .requestIdleCallback
    if (typeof idle === 'function') idle(() => resolve(), { timeout: 2000 })
    else setTimeout(resolve, 300)
  })
}

/**
 * Gzip in the browser. A serialized DOM is highly repetitive text, so this
 * typically cuts the payload by an order of magnitude. Browsers without
 * CompressionStream simply upload the raw JSON.
 */
async function gzip(text: string): Promise<Blob> {
  const raw = new Blob([text], { type: 'application/json' })
  if (typeof CompressionStream === 'undefined') return raw

  const stream = raw.stream().pipeThrough(new CompressionStream('gzip'))
  return new Response(stream).blob()
}
